/**
 * Copyright Zendesk, Inc.
 *
 * Use of this source code is governed under the Apache License, Version 2.0
 * found at http://www.apache.org/licenses/LICENSE-2.0.
 */

import * as React from 'react';
import React__default, { cloneElement, Children, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import styled, { css, ThemeContext } from 'styled-components';
import { usePagination } from '@zendeskgarden/container-pagination';
import { getControlledValue } from '@zendeskgarden/container-utilities';
import { getColor, retrieveComponentStyles, DEFAULT_THEME, getLineHeight } from '@zendeskgarden/react-theming';
import { math } from 'polished';

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends$4() {
  _extends$4 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$4.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _extends$3() { _extends$3 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3.apply(this, arguments); }

var _ref$3 = /*#__PURE__*/React.createElement("path", {
  fill: "currentColor",
  d: "M10.39 12.688a.5.5 0 01-.718.69l-.062-.066-4-5a.5.5 0 01-.054-.542l.054-.082 4-5a.5.5 0 01.83.55l-.05.074L6.641 8l3.75 4.688z"
});

function SvgChevronLeftStroke(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref$3);
}

function _extends$2() { _extends$2 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2.apply(this, arguments); }

var _ref$2 = /*#__PURE__*/React.createElement("path", {
  fill: "currentColor",
  d: "M5.61 3.312a.5.5 0 01.718-.69l.062.066 4 5a.5.5 0 01.054.542l-.054.082-4 5a.5.5 0 01-.83-.55l.05-.074L9.359 8l-3.75-4.688z"
});

function SvgChevronRightStroke(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref$2);
}

var COMPONENT_ID$6 = 'pagination.pagination_view';
var StyledPagination = styled.ul.attrs({
  'data-garden-id': COMPONENT_ID$6,
  'data-garden-version': '8.42.2'
}).withConfig({
  displayName: "StyledPagination",
  componentId: "sc-1b7nye9-0"
})(["direction:", ";display:flex;justify-content:center;margin:0;padding:0;white-space:nowrap;color:", ";:focus{outline:none;}", ";"], function (props) {
  return props.theme.rtl && 'rtl';
}, function (props) {
  return getColor('neutralHue', 600, props.theme);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$6, props);
});
StyledPagination.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$5 = 'pagination.page';
var colorStyles = function colorStyles(props) {
  var defaultColor = getColor('neutralHue', 600, props.theme);
  var hoverForegroundColor = getColor('neutralHue', 700, props.theme);
  var hoverBackgroundColor = getColor('primaryHue', 600, props.theme, 0.08);
  var boxShadowColor = getColor('primaryHue', 600, props.theme, 0.35);
  var activeForegroundColor = getColor('neutralHue', 800, props.theme);
  var activeBackgroundColor = getColor('primaryHue', 600, props.theme, 0.2);
  var currentForegroundColor = activeForegroundColor;
  var currentBackgroundColor = hoverBackgroundColor;
  var currentHoverBackgroundColor = getColor('primaryHue', 600, props.theme, 0.16);
  var currentActiveBackgroundColor = getColor('primaryHue', 600, props.theme, 0.28);
  return css(["color:", ";&:hover{background-color:", ";color:", ";}&[data-garden-focus-visible]{box-shadow:inset ", ";}&:active,&[data-garden-focus-visible]:active{background-color:", ";color:", ";}&[aria-current='true']{background-color:", ";color:", ";}&[aria-current='true']:hover{background-color:", ";}&[aria-current='true']:active{background-color:", ";}:disabled,[aria-disabled='true']{background-color:transparent;color:", ";}"], defaultColor, hoverBackgroundColor, hoverForegroundColor, props.theme.shadows.md(boxShadowColor), activeBackgroundColor, activeForegroundColor, currentBackgroundColor, currentForegroundColor, currentHoverBackgroundColor, currentActiveBackgroundColor, getColor('grey', 300, props.theme));
};
var sizeStyles$2 = function sizeStyles(props) {
  var fontSize = props.theme.fontSizes.md;
  var height = "".concat(props.theme.space.base * 8, "px");
  var lineHeight = getLineHeight(height, fontSize);
  var padding = "".concat(props.theme.space.base * 1.5, "px");
  return css(["padding:0 ", ";height:", ";line-height:", ";font-size:", ";"], padding, height, lineHeight, fontSize);
};
var StyledPageBase = styled.li.attrs({
  'data-garden-id': COMPONENT_ID$5,
  'data-garden-version': '8.42.2'
}).withConfig({
  displayName: "StyledPageBase",
  componentId: "sc-lw1w9j-0"
})(["box-sizing:border-box;display:inline-block;transition:box-shadow 0.1s ease-in-out,background-color 0.25s ease-in-out,color 0.25s ease-in-out;visibility:", ";border-radius:", ";cursor:pointer;overflow:hidden;text-align:center;text-overflow:ellipsis;user-select:none;", ";&:focus{outline:none;}&[aria-current='true']{font-weight:", ";}:disabled,[aria-disabled='true']{cursor:default;}", ";", ";"], function (props) {
  return props.hidden && 'hidden';
}, function (props) {
  return props.theme.borderRadii.md;
}, function (props) {
  return sizeStyles$2(props);
}, function (props) {
  return props.theme.fontWeights.semibold;
}, function (props) {
  return colorStyles(props);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$5, props);
});
StyledPageBase.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$4 = 'pagination.page';
var sizeStyles$1 = function sizeStyles(props) {
  var height = "".concat(props.theme.space.base * 8, "px");
  return css(["min-width:", ";max-width:", ";&[aria-current='true']{max-width:none;}"], height, math("".concat(height, " * 2")));
};
var StyledPage = styled(StyledPageBase).attrs({
  'data-garden-id': COMPONENT_ID$4,
  'data-garden-version': '8.42.2'
}).withConfig({
  displayName: "StyledPage",
  componentId: "sc-1k0een3-0"
})(["margin-left:", ";", ";&[aria-current=\"true\"]{font-weight:", ";}&", "{margin-left:0;}", ";"], function (props) {
  return "".concat(props.theme.space.base, "px");
}, function (props) {
  return sizeStyles$1(props);
}, function (props) {
  return props.theme.fontWeights.semibold;
}, function (props) {
  return props.theme.rtl ? ':last-of-type' : ':first-of-type';
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$4, props);
});
StyledPage.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$3 = 'cursor_pagination';
var StyledCursorPagination = styled.nav.attrs({
  'data-garden-id': COMPONENT_ID$3,
  'data-garden-version': '8.42.2'
}).withConfig({
  displayName: "StyledCursorPagination",
  componentId: "sc-qmfecg-0"
})(["display:flex;justify-content:center;", ";"], function (props) {
  return retrieveComponentStyles(COMPONENT_ID$3, props);
});
StyledCursorPagination.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$2 = 'cursor_pagination.cursor';
var StyledCursor = styled(StyledPageBase).attrs({
  'data-garden-id': COMPONENT_ID$2,
  'data-garden-version': '8.42.2',
  as: 'button'
}).withConfig({
  displayName: "StyledCursor",
  componentId: "sc-507ee-0"
})(["display:flex;align-items:center;border:none;background:transparent;padding:", ";overflow:visible;&:not(", "-of-type){margin-right:", "px;}", ";"], function (props) {
  return "0px ".concat(props.theme.space.base * 2, "px");
}, function (props) {
  return props.theme.rtl ? ':first' : ':last';
}, function (props) {
  return props.theme.space.base;
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$2, props);
});
StyledCursor.defaultProps = {
  theme: DEFAULT_THEME
};

var _excluded$5 = ["children"];
var marginStyles = function marginStyles(props) {
  var type = props.type,
      theme = props.theme;
  var margin = theme.space.base;
  if (theme.rtl) {
    return css(["margin-", ":", "px;"], type === 'last' || type === 'next' ? 'right' : 'left', margin);
  }
  return css(["margin-", ":", "px;"], type === 'first' || type === 'previous' ? 'right' : 'left', margin);
};
var StyledIcon = styled(function (_ref) {
  var children = _ref.children,
      props = _objectWithoutProperties(_ref, _excluded$5);
  return cloneElement(Children.only(children), props);
}).withConfig({
  displayName: "StyledIcon",
  componentId: "sc-2vzk6e-0"
})(["", " transform:", ";"], marginStyles, function (props) {
  return props.theme.rtl && 'rotate(180deg)';
});
StyledIcon.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$1 = 'pagination.gap';
var sizeStyles = function sizeStyles(props) {
  var shift = 2;
  var marginTop = "-".concat(shift, "px");
  var fontSize = math("".concat(props.theme.fontSizes.md, " + ").concat(shift));
  return css(["margin-top:", ";font-size:", ";"], marginTop, fontSize);
};
var StyledGap = styled(StyledPage).attrs({
  'data-garden-id': COMPONENT_ID$1,
  'data-garden-version': '8.42.2'
}).withConfig({
  displayName: "StyledGap",
  componentId: "sc-1hqjltf-0"
})(["cursor:default;", ";&:hover{background-color:transparent;color:inherit;}", ";"], function (props) {
  return sizeStyles(props);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$1, props);
});
StyledGap.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID = 'pagination.navigation';
var StyledNavigation = styled(StyledPage).attrs({
  'data-garden-id': COMPONENT_ID,
  'data-garden-version': '8.42.2'
}).withConfig({
  displayName: "StyledNavigation",
  componentId: "sc-184uuwe-0"
})(["display:flex;align-items:center;justify-content:center;", ";"], function (props) {
  return retrieveComponentStyles(COMPONENT_ID, props);
});
StyledNavigation.defaultProps = {
  theme: DEFAULT_THEME
};

var _excluded$4 = ["currentPage", "transformPageProps", "totalPages", "pagePadding", "pageGap", "onChange"];
var PREVIOUS_KEY = 'previous';
var NEXT_KEY = 'next';
var Pagination = React__default.forwardRef(function (_ref, ref) {
  var controlledCurrentPage = _ref.currentPage,
      transformPageProps = _ref.transformPageProps,
      totalPages = _ref.totalPages,
      pagePadding = _ref.pagePadding,
      pageGap = _ref.pageGap,
      onChange = _ref.onChange,
      otherProps = _objectWithoutProperties(_ref, _excluded$4);
  var _useState = useState(),
      _useState2 = _slicedToArray(_useState, 2),
      focusedItem = _useState2[0],
      setFocusedItem = _useState2[1];
  var _useState3 = useState(1),
      _useState4 = _slicedToArray(_useState3, 2),
      internalCurrentPage = _useState4[0],
      setCurrentPage = _useState4[1];
  var currentPage = getControlledValue(controlledCurrentPage, internalCurrentPage);
  var theme = useContext(ThemeContext);
  var _usePagination = usePagination({
    rtl: theme.rtl,
    focusedItem: focusedItem,
    selectedItem: currentPage,
    onFocus: function onFocus(item) {
      setFocusedItem(item);
    },
    onSelect: function onSelect(item) {
      var updatedCurrentPage = item;
      var updatedFocusedKey = focusedItem;
      if (updatedCurrentPage === PREVIOUS_KEY && currentPage > 1) {
        updatedCurrentPage = currentPage - 1;
        if (updatedCurrentPage === 1 && focusedItem === PREVIOUS_KEY) {
          updatedFocusedKey = 1;
        }
      } else if (updatedCurrentPage === NEXT_KEY && currentPage < totalPages) {
        updatedCurrentPage = currentPage + 1;
        if (updatedCurrentPage === totalPages && updatedFocusedKey === NEXT_KEY) {
          updatedFocusedKey = totalPages;
        }
      }
      if (onChange && updatedCurrentPage !== undefined) {
        onChange(updatedCurrentPage);
      }
      setFocusedItem(updatedFocusedKey);
      setCurrentPage(updatedCurrentPage);
    }
  }),
      getContainerProps = _usePagination.getContainerProps,
      getPageProps = _usePagination.getPageProps,
      getPreviousPageProps = _usePagination.getPreviousPageProps,
      getNextPageProps = _usePagination.getNextPageProps;
  var getTransformedProps = function getTransformedProps(pageType) {
    var props = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    if (transformPageProps) {
      return transformPageProps(pageType, props);
    }
    return props;
  };
  var renderPreviousPage = function renderPreviousPage(rtl) {
    var isFirstPageSelected = totalPages > 0 && currentPage === 1;
    var focusRef = React__default.createRef();
    return React__default.createElement(StyledNavigation, getTransformedProps('previous',
    isFirstPageSelected ? {
      hidden: true
    } : getPreviousPageProps({
      role: null,
      key: PREVIOUS_KEY,
      isFocused: focusedItem === PREVIOUS_KEY,
      item: PREVIOUS_KEY,
      ref: focusRef,
      focusRef: focusRef
    })), rtl ? React__default.createElement(SvgChevronRightStroke, null) : React__default.createElement(SvgChevronLeftStroke, null));
  };
  var renderNextPage = function renderNextPage(rtl) {
    var isLastPageSelected = currentPage === totalPages;
    var focusRef = React__default.createRef();
    return React__default.createElement(StyledNavigation, getTransformedProps('next',
    isLastPageSelected ? {
      hidden: true
    } : getNextPageProps({
      role: null,
      item: NEXT_KEY,
      key: NEXT_KEY,
      isFocused: focusedItem === NEXT_KEY,
      ref: focusRef,
      focusRef: focusRef
    })), rtl ? React__default.createElement(SvgChevronLeftStroke, null) : React__default.createElement(SvgChevronRightStroke, null));
  };
  var createGap = function createGap(pageIndex) {
    return React__default.createElement(StyledGap, getTransformedProps('gap', {
      key: "gap-".concat(pageIndex)
    }), "\u2026");
  };
  var createPage = function createPage(pageIndex) {
    var focusRef = React__default.createRef();
    return React__default.createElement(StyledPage, getTransformedProps('page', getPageProps({
      role: null,
      key: pageIndex,
      item: pageIndex,
      page: pageIndex,
      title: pageIndex.toString(),
      current: currentPage === pageIndex,
      ref: focusRef,
      focusRef: focusRef
    })), pageIndex);
  };
  var renderPages = function renderPages() {
    var pages = [];
    var PADDING = pagePadding;
    var GAP = pageGap;
    for (var pageIndex = 1; pageIndex <= totalPages; pageIndex++) {
      if (pageIndex === currentPage || pageIndex < GAP || pageIndex > totalPages - GAP + 1) {
        pages.push(createPage(pageIndex));
        continue;
      }
      var minimum = void 0;
      var maximum = void 0;
      if (currentPage <= GAP + PADDING) {
        minimum = GAP + 1;
        maximum = minimum + PADDING * 2;
      } else if (currentPage >= totalPages - GAP - PADDING) {
        maximum = totalPages - GAP;
        minimum = maximum - PADDING * 2;
      } else {
        minimum = currentPage - PADDING;
        maximum = currentPage + PADDING;
      }
      if (pageIndex >= minimum && pageIndex <= currentPage || pageIndex >= currentPage && pageIndex <= maximum) {
        pages.push(createPage(pageIndex));
        continue;
      }
      if (pageIndex === GAP) {
        if (minimum > GAP + 1 && currentPage > GAP + PADDING + 1) {
          pages.push(createGap(pageIndex));
        } else {
          pages.push(createPage(pageIndex));
        }
        continue;
      }
      if (pageIndex === totalPages - GAP + 1) {
        if (maximum < totalPages - GAP && currentPage < totalPages - GAP - PADDING) {
          pages.push(createGap(pageIndex));
        } else {
          pages.push(createPage(pageIndex));
        }
        continue;
      }
    }
    return pages;
  };
  return React__default.createElement(StyledPagination, _extends$4({}, getContainerProps(_objectSpread2({
    role: null
  }, otherProps)), {
    ref: ref
  }), renderPreviousPage(theme.rtl), totalPages > 0 && renderPages(), renderNextPage(theme.rtl));
});
Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  pagePadding: PropTypes.number,
  pageGap: PropTypes.number,
  onChange: PropTypes.func,
  transformPageProps: PropTypes.func
};
Pagination.defaultProps = {
  pagePadding: 2,
  pageGap: 2
};

function _extends$1() { _extends$1 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1.apply(this, arguments); }

var _ref$1 = /*#__PURE__*/React.createElement("path", {
  fill: "currentColor",
  d: "M7.812 13.39a.5.5 0 01-.64-.012l-.062-.066-4-5a.5.5 0 01-.054-.542l.054-.082 4-5a.5.5 0 01.83.55l-.05.074L4.141 8l3.75 4.688a.5.5 0 01-.079.702zm5 0a.5.5 0 01-.64-.012l-.062-.066-4-5a.5.5 0 01-.054-.542l.054-.082 4-5a.5.5 0 01.83.55l-.05.074L9.141 8l3.75 4.688a.5.5 0 01-.079.702z"
});

function SvgChevronDoubleLeftStroke(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref$1);
}

var _excluded$3 = ["children"];
var First = React__default.forwardRef(function (_ref, ref) {
  var children = _ref.children,
      other = _objectWithoutProperties(_ref, _excluded$3);
  return React__default.createElement(StyledCursor, _extends$4({
    ref: ref
  }, other), React__default.createElement(StyledIcon, {
    type: "first"
  }, React__default.createElement(SvgChevronDoubleLeftStroke, null)), React__default.createElement("span", null, children));
});
First.displayName = 'First';

var _excluded$2 = ["children"];
var Next = React__default.forwardRef(function (_ref, ref) {
  var children = _ref.children,
      other = _objectWithoutProperties(_ref, _excluded$2);
  return React__default.createElement(StyledCursor, _extends$4({
    ref: ref,
    as: "button"
  }, other), React__default.createElement("span", null, children), React__default.createElement(StyledIcon, {
    type: "next"
  }, React__default.createElement(SvgChevronRightStroke, null)));
});
Next.displayName = 'Next';

var _excluded$1 = ["children"];
var Previous = React__default.forwardRef(function (_ref, ref) {
  var children = _ref.children,
      other = _objectWithoutProperties(_ref, _excluded$1);
  return React__default.createElement(StyledCursor, _extends$4({
    ref: ref,
    as: "button"
  }, other), React__default.createElement(StyledIcon, {
    type: "previous"
  }, React__default.createElement(SvgChevronLeftStroke, null)), React__default.createElement("span", null, children));
});
Previous.displayName = 'Previous';

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

var _ref = /*#__PURE__*/React.createElement("path", {
  fill: "currentColor",
  d: "M8.188 2.61a.5.5 0 01.64.013l.062.065 4 5a.5.5 0 01.054.542l-.054.082-4 5a.5.5 0 01-.83-.55l.05-.074L11.859 8l-3.75-4.688a.5.5 0 01.079-.702zm-5 0a.5.5 0 01.64.013l.062.065 4 5a.5.5 0 01.054.542l-.054.082-4 5a.5.5 0 01-.83-.55l.05-.074L6.859 8l-3.75-4.688a.5.5 0 01.079-.702z"
});

function SvgChevronDoubleRightStroke(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref);
}

var _excluded = ["children"];
var Last = React__default.forwardRef(function (_ref, ref) {
  var children = _ref.children,
      other = _objectWithoutProperties(_ref, _excluded);
  return React__default.createElement(StyledCursor, _extends$4({
    ref: ref,
    as: "button"
  }, other), React__default.createElement("span", null, children), React__default.createElement(StyledIcon, {
    type: "last"
  }, React__default.createElement(SvgChevronDoubleRightStroke, null)));
});
Last.displayName = 'Last';

var CursorPagination = React__default.forwardRef(function (props, ref) {
  return React__default.createElement(StyledCursorPagination, _extends$4({
    ref: ref
  }, props));
});
CursorPagination.First = First;
CursorPagination.Next = Next;
CursorPagination.Previous = Previous;
CursorPagination.Last = Last;
CursorPagination.displayName = 'CursorPagination';

export { CursorPagination, Pagination };
